import { create } from "zustand";

/**
 * Colour theme. The preference is stored under the same localStorage key and
 * values the API reference (Scalar, same origin at /api/docs) uses, so one
 * choice applies to both. index.html applies it before first paint.
 */
export type ThemePref = "system" | "light" | "dark";
export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "colorMode";
const media = typeof window !== "undefined" ? window.matchMedia?.("(prefers-color-scheme: dark)") : undefined;

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

function resolve(pref: ThemePref): Theme {
  return pref === "system" ? (media?.matches ? "dark" : "light") : pref;
}

function apply(theme: Theme) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
}

interface ThemeState {
  pref: ThemePref;
  theme: Theme;
  setPref: (pref: ThemePref) => void;
}

export const useTheme = create<ThemeState>((set) => {
  const pref = readPref();
  return {
    pref,
    theme: resolve(pref),
    setPref: (next) => {
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // Private mode / blocked storage: still switch for this session.
      }
      const theme = resolve(next);
      apply(theme);
      set({ pref: next, theme });
    },
  };
});

if (typeof window !== "undefined") {
  apply(useTheme.getState().theme);
  // Follow the OS while on "system".
  media?.addEventListener("change", () => {
    const { pref } = useTheme.getState();
    if (pref !== "system") return;
    const theme = resolve(pref);
    apply(theme);
    useTheme.setState({ theme });
  });
  // Another tab (or the API reference) changed the preference.
  window.addEventListener("storage", (e) => {
    if (e.key !== THEME_STORAGE_KEY) return;
    const pref = readPref();
    const theme = resolve(pref);
    apply(theme);
    useTheme.setState({ pref, theme });
  });
}
